import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Cart } from "Types/Cart";
import { ProductPackerDTO } from "Types/ProductPackerDTO";
import { isClientPromotionActive, isPromoUsageLimitReached } from "Utilities/PromotionDateUtils";


interface CartError {
  message: string;
  type: "network" | "server" | "validation";
  timestamp: number;
}

interface CartState {
  isInteracting: boolean;
  cartList: Array<Cart>;
  cartCount: number;
  cartProductList: ProductPackerDTO | null;
  cartSubTotal: number;
  cartDiscount: number;
  rewardApplied: boolean;
  rewardValue: number;
  loyaltyMaxRedeemPercentage: number;
  loyaltyRedeemValue: number;
  promoBuyQty: number;
  error: CartError | null;
  selectedGrandPromo: any | null;
  selectedCartPromoId: string | null;
}

const storedCart = localStorage.getItem("cart");

const initialState: CartState = {
  isInteracting: false,
  cartList: storedCart ? JSON.parse(storedCart) : [],
  cartCount: 0,
  cartProductList: null,
  cartSubTotal: 0,
  cartDiscount: 0,
  rewardApplied: false,
  rewardValue: 0,
  loyaltyMaxRedeemPercentage: 0,
  loyaltyRedeemValue: 0,
  promoBuyQty: 0,
  error: null,
  selectedGrandPromo: null,
  selectedCartPromoId: null,
};

const CartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<Cart | Array<Cart>>) => {
      const payloadArray = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      payloadArray.forEach((item) => {
        let index = state.cartList.findIndex(
          (cartItem) =>
            cartItem.productId === item.productId &&
            cartItem.size === item.size,
        );
        if (index === -1) {
          state.cartList.push(item);
          index = state.cartList.indexOf(item);
        } else {
          const newQty = state.cartList[index].quantity + item.quantity;
          const maxQty = item.availableQuantity ?? 0;
          state.cartList[index].quantity = maxQty > 0 ? Math.min(newQty, maxQty) : newQty;
        }
      });

      updateLocalStorage(state.cartList);
    },

    removeFromCart: (
      state,
      action: PayloadAction<{ productId: string; size?: string }>,
    ) => {
      state.cartList = state.cartList.filter(
        (item) =>
          !(
            item.productId === action.payload.productId &&
            item.size === action.payload.size
          ),
      );
      updateLocalStorage(state.cartList);
    },

    removeFromCartWithSync: (
      state,
      action: PayloadAction<{ productId: string; size?: string }>,
    ) => {
      state.cartList = state.cartList.filter(
        (item) =>
          !(
            item.productId === action.payload.productId &&
            item.size === action.payload.size
          ),
      );
      updateLocalStorage(state.cartList);
    },

    setCartCount: (state, action: PayloadAction<number>) => {
      state.cartCount = action.payload;
    },

    increaseQuantity: (
      state,
      action: PayloadAction<{ productId: string; availableQuantity: number }>,
    ) => {
      const index = state.cartList.findIndex(
        (cartItem) => cartItem.productId === action.payload.productId,
      );

      if (
        action.payload.availableQuantity - 1 >
        state.cartList[index].quantity
      ) {
        state.cartList[index].quantity += 1;
        updateLocalStorage(state.cartList);
      }
    },

    decreaseQuantity: (state, action: PayloadAction<string>) => {
      const index = state.cartList.findIndex(
        (cartItem) => cartItem.productId === action.payload,
      );
      state.cartList[index].quantity -= 1;
      updateLocalStorage(state.cartList);
    },

    calculateSubTotal: (state) => {
      let sum = 0;
      state.cartList.forEach((item) => {
        sum += item.ecomUnitPrice * item.quantity;
      });
      state.cartSubTotal = sum;
    },

    calculateDiscount: (state) => {
      let sum = 0;

      state.cartList.forEach((item) => {
        const product = state.cartProductList?.products?.find(
          (p) => p.product.item === item.productId,
        );

        const basePrice = Number(item.ecomUnitPrice) || 0;
        let finalUnitPrice = basePrice;

        const now = new Date();
        const promos = (product?.promotions || []).filter((p) =>
          isClientPromotionActive(p, now)
        );

        // Match allCartPromotions filter: no-buyQty promos always qualify; skip exhausted promos.
        let byValuePromos = promos.filter(
          (p) =>
            p.promotionBy === "ByValue" &&
            !isPromoUsageLimitReached(p) &&
            (typeof p.buyQty !== "number" || item.quantity >= p.buyQty),
        );

        // Only apply a discount when the user has explicitly selected one.
        // When selectedCartPromoId is null → no promo selected → no discount.
        // When selectedCartPromoId is a ByQuantity key → byValuePromos filters to zero (suppressed).
        if (state.selectedCartPromoId === null) {
          byValuePromos = [];
        } else {
          byValuePromos = byValuePromos.filter(
            (p) => String(p.promotionId) === state.selectedCartPromoId,
          );
        }

        if (!byValuePromos || byValuePromos.length === 0) {
          // console.log("Sum : ", sum);
          sum += basePrice * item.quantity;
          return sum;
        }

        let price = basePrice;

        for (const promo of byValuePromos) {
          if (promo.promotionType === "%") {
            price -= (basePrice * promo.promotion) / 100;
          }

          if (promo.promotionType === "INR") {
            price -= promo.promotion;
          }
        }

        finalUnitPrice = price;

        sum += finalUnitPrice * item.quantity;
      });

      state.cartDiscount = sum;
      if (state.rewardApplied) {
        const maxByPercentage =
          (state.cartDiscount * state.loyaltyMaxRedeemPercentage) / 100;

        state.rewardValue = Math.min(maxByPercentage, state.loyaltyRedeemValue);
      }
    },

    clearCart: (state) => {
      state.cartList = [];
      localStorage.removeItem("cart");
      localStorage.removeItem("isCartFetched");
    },

    putToCart: (state, action: PayloadAction<Cart | Array<Cart>>) => {
      const payloadArray = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];
      state.cartList = payloadArray;
      updateLocalStorage(state.cartList);
    },

    appendToCart: (state, action: PayloadAction<Cart | Array<Cart>>) => {
      const payloadArray = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      const storedCart = localStorage.getItem("cart");
      let currentCart: Array<Cart> = storedCart ? JSON.parse(storedCart) : [];

      payloadArray.forEach((item) => {
        const existing = currentCart.find(
          (c) => c.productId === item.productId,
        );
        if (existing) {
          existing.quantity += item.quantity;
        } else {
          currentCart.push(item);
        }
      });
      state.cartList = currentCart;
      updateLocalStorage(currentCart);
    },

    addToProductCart: (state, action: PayloadAction<ProductPackerDTO>) => {
      state.cartProductList = action.payload;
    },

    removeFromProductCart: (state, action: PayloadAction<string>) => {
      if (state.cartProductList && state.cartProductList.products.length > 0) {
        state.cartProductList.products = state.cartProductList.products.filter(
          (item) => item.product.item !== action.payload,
        );
      }
    },

    updateQuantity: (state, action) => {
      const { productId, size, quantity } = action.payload;
      const index = state.cartList.findIndex(
        (cartItem) =>
          cartItem.productId === productId && cartItem.size === size,
      );

      state.cartList[index].quantity = quantity;
      updateLocalStorage(state.cartList);
    },

    setRewardApplied: (state, action: PayloadAction<{ applied: boolean }>) => {
      state.rewardApplied = action.payload.applied;

      if (!action.payload.applied) {
        state.rewardValue = 0;
        return;
      }

      const maxByPercentage =
        (state.cartDiscount * state.loyaltyMaxRedeemPercentage) / 100;

      state.rewardValue = Math.min(maxByPercentage, state.loyaltyRedeemValue);
    },

    setLoyaltyConfig: (
      state,
      action: PayloadAction<{
        maxRedeemPercentage: number;
        redeemValue: number;
      }>,
    ) => {
      state.loyaltyMaxRedeemPercentage = action.payload.maxRedeemPercentage;
      state.loyaltyRedeemValue = action.payload.redeemValue;
    },

    recalculateReward: (state) => {
      if (!state.rewardApplied) {
        state.rewardValue = 0;
        return;
      }

      const maxByPercentage =
        (state.cartDiscount * state.loyaltyMaxRedeemPercentage) / 100;

      state.rewardValue = Math.min(maxByPercentage, state.loyaltyRedeemValue);
    },

    clearReward: (state) => {
      state.rewardApplied = false;
      state.rewardValue = 0;
    },
    syncCartWithServer: (state, action: PayloadAction<Array<Cart>>) => {
      state.cartList = action.payload;
      updateLocalStorage(state.cartList);
    },

    mergeWithGuestCart: (state, action: PayloadAction<Array<Cart>>) => {
      const serverCart = action.payload;
      const guestCart = state.cartList;

      const mergedCart = [...guestCart];

      serverCart.forEach((serverItem) => {
        const existingIndex = mergedCart.findIndex(
          (guestItem) =>
            guestItem.productId === serverItem.productId &&
            guestItem.size === serverItem.size,
        );

        if (existingIndex === -1) {
          mergedCart.push(serverItem);
        } else {
          mergedCart[existingIndex].quantity = serverItem.quantity;
        }
      });

      state.cartList = mergedCart;
      updateLocalStorage(mergedCart);
    },

    clearCartOnLogout: (state) => {
      state.cartList = [];
      state.cartProductList = null;
      localStorage.removeItem("cart");
      localStorage.removeItem("isCartFetched");
    },
    setCartError: (state, action: PayloadAction<CartError | null>) => {
      state.error = action.payload;
    },
    setSelectedGrandPromo: (state, action: PayloadAction<any | null>) => {
      state.selectedGrandPromo = action.payload;
    },
    setSelectedCartPromoId: (state, action: PayloadAction<string | null>) => {
      state.selectedCartPromoId = action.payload;
    },
    setInteracting: (state, action: PayloadAction<boolean>) => {
      state.isInteracting = action.payload;
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  removeFromCartWithSync,
  setCartCount,
  increaseQuantity,
  decreaseQuantity,
  calculateSubTotal,
  calculateDiscount,
  clearCart,
  putToCart,
  appendToCart,
  addToProductCart,
  removeFromProductCart,
  updateQuantity,
  setLoyaltyConfig,
  recalculateReward,
  setRewardApplied,
  clearReward,
  syncCartWithServer,
  mergeWithGuestCart,
  clearCartOnLogout,
  setCartError,
  setSelectedGrandPromo,
  setSelectedCartPromoId,
  setInteracting,
} = CartSlice.actions;

export default CartSlice.reducer;

const updateLocalStorage = (cartList: Array<Cart> | string) => {
  localStorage.setItem("cart", JSON.stringify(cartList));
};
